import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SSLogoIcon } from './SSLogo';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';
import { safePost } from '../utils/safeFetch';
import {
  MessageCircle, X, Send, Sparkles, User, Loader2,
  ShoppingBag, TrendingUp, CreditCard, HelpCircle,
  Zap, Shield, Truck, Headphones, Star,
  AlertCircle, Settings, DollarSign, Package, Image as ImageIcon
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const QUICK_TOPICS = [
  { icon: Zap, text: 'كيف أبدأ؟', color: 'bg-emerald-500' },
  { icon: TrendingUp, text: 'نظام العمولات', color: 'bg-blue-500' },
  { icon: CreditCard, text: 'طريقة الدفع', color: 'bg-amber-500' },
  { icon: Truck, text: 'كيف يتم الشحن؟', color: 'bg-purple-500' },
  { icon: ShoppingBag, text: 'أرني أرخص المنتجات', color: 'bg-rose-500' },
  { icon: Shield, text: 'هل المنصة آمنة؟', color: 'bg-cyan-500' },
];
const SUPPORT_REASONS = [
  { icon: HelpCircle, text: 'استفسار عام', key: 'general' },
  { icon: AlertCircle, text: 'مشكلة تقنية', key: 'technical' },
  { icon: DollarSign, text: 'كيف أشتري العملات؟', key: 'coins' },
  { icon: Package, text: 'مشكلة في طلب أو شحنة', key: 'order' },
  { icon: TrendingUp, text: 'العمولات والمهام', key: 'commission' },
  { icon: Settings, text: 'مشكلة في حسابي', key: 'account' },
];

export default function AIChatWidget() {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('ai'); // 'ai' | chat_id
  const [messages, setMessages] = useState(() => {
    try { const s = sessionStorage.getItem('ss_ai_msgs'); return s ? JSON.parse(s) : []; } catch { return []; }
  });
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => {
    let s = sessionStorage.getItem('ss_chat_session');
    if (!s) { s = `chat_${Date.now()}_${Math.random().toString(36).slice(2,8)}`; sessionStorage.setItem('ss_chat_session', s); }
    return s;
  });
  const [hasNewMsg, setHasNewMsg] = useState(false);
  const [showTopics, setShowTopics] = useState(true);
  const [humanStep, setHumanStep] = useState(null);
  const [humanData, setHumanData] = useState({ is_seller: null, reason: '', name: '', phone: '', email: '' });
  const [liveChatId, setLiveChatId] = useState(null);
  const [liveData, setLiveData] = useState({ messages: [], status: '', agent_name: '', agent_photo: '', rating: 0 });
  const [chatHistory, setChatHistory] = useState([]);
  const [ratingValue, setRatingValue] = useState(0);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const { user } = useAuth();

  // Save AI messages to session
  useEffect(() => { sessionStorage.setItem('ss_ai_msgs', JSON.stringify(messages)); }, [messages]);

  // Clear AI messages after 24h
  useEffect(() => {
    const ts = sessionStorage.getItem('ss_ai_msgs_ts');
    if (ts && Date.now() - parseInt(ts) > 86400000) { setMessages([]); sessionStorage.removeItem('ss_ai_msgs'); }
    if (!ts) sessionStorage.setItem('ss_ai_msgs_ts', Date.now().toString());
  }, []);

  const [closedTabIds, setClosedTabIds] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('ss_closed_tabs') || '[]'); } catch { return []; }
  });
  const [panelExpanded, setPanelExpanded] = useState(true);

  // Load chat history - only when open
  useEffect(() => {
    if (!open) return;
    const loadHistory = async () => {
      try {
        const uid = user?._id || sessionStorage.getItem('ss_chat_session') || '';
        const url = user?._id ? `${API_URL}/api/live-chat/user-history/${uid}` : `${API_URL}/api/live-chat/history/${sessionId}`;
        const r = await fetch(url);
        const d = await r.json();
        if (Array.isArray(d)) setChatHistory(d.filter(c => !closedTabIds.includes(c.id)));
      } catch {}
    };
    loadHistory();
    const interval = setInterval(loadHistory, 5000);
    return () => clearInterval(interval);
  }, [user, sessionId, closedTabIds, open]);

  // Poll active live chat
  useEffect(() => {
    if (!liveChatId) return;
    const poll = setInterval(async () => {
      try {
        const r = await fetch(`${API_URL}/api/live-chat/${liveChatId}/messages`);
        const d = await r.json();
        setLiveData(d);
        if (d.status === 'active' && humanStep === 'waiting') setHumanStep('live');
        if (d.status === 'closed' && humanStep !== null) {
          setHumanStep('closed');
        }
      } catch {}
    }, 3000);
    return () => clearInterval(poll);
  }, [liveChatId, humanStep]);

  // Smart scroll - only on genuinely new messages, not tab switch or poll
  const prevMsgLen = useRef(0);
  const prevLiveLen = useRef(0);
  const prevActiveTab = useRef('ai');
  useEffect(() => {
    const aiLen = messages.length;
    if (activeTab === 'ai' && prevActiveTab.current === 'ai' && aiLen > prevMsgLen.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
    prevMsgLen.current = aiLen;
    prevActiveTab.current = activeTab;
  }, [messages.length, activeTab]);
  useEffect(() => {
    const liveLen = liveData.messages?.length || 0;
    if (activeTab !== 'ai' && liveLen > prevLiveLen.current && prevLiveLen.current > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
    prevLiveLen.current = liveLen;
  }, [liveData.messages?.length, activeTab]);
  useEffect(() => {
    if (open) { setTimeout(() => document.getElementById('chat-input-main')?.focus(), 300); setHasNewMsg(false); }
  }, [open]);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ id: 'w', role: 'assistant', time: new Date().toISOString(), text: 'مرحباً بك في SHOP & SHIP! 👋\n\nأنا مساعدك الذكي. اختر موضوعاً أو اكتب سؤالك 💬' }]);
    }
  }, [open]);

  const HUMAN_KEYWORDS = ['أريد التحدث مع موظف', 'أريد موظف', 'تحدث مع إنسان', 'اريد التحدث مع موظف', 'موظف بشري', 'talk to human', 'human agent', 'real person', 'speak to agent', 'talk to agent', 'representative', 'customer service', 'live chat', 'live support'];
  const isHumanRequest = (msg) => HUMAN_KEYWORDS.some(k => msg.toLowerCase().includes(k.toLowerCase()));

  const startHumanFlow = () => { setShowTopics(false); setHumanStep('type'); setActiveTab('ai');
    setMessages(p => [...p, { id: Date.now(), role: 'assistant', time: new Date().toISOString(), text: 'بالطبع! سأحوّلك لفريق الدعم. 🎧\n\nأولاً، هل أنت تاجر مسجّل لدينا أم لا تزال تدرس الموضوع؟' }]); };
  const selectType = (s) => { setHumanData(p => ({...p, is_seller: s})); setHumanStep('reason');
    setMessages(p => [...p, { id: Date.now(), role: 'user', time: new Date().toISOString(), text: s ? 'نعم، أنا تاجر مسجّل' : 'لا، أدرس الموضوع' }, { id: Date.now()+1, role: 'assistant', time: new Date().toISOString(), text: 'ما سبب تواصلك معنا؟' }]); };
  const selectReason = (r) => { setHumanData(p => ({...p, reason: r.key})); setHumanStep('form');
    setMessages(p => [...p, { id: Date.now(), role: 'user', time: new Date().toISOString(), text: r.text }, { id: Date.now()+1, role: 'assistant', time: new Date().toISOString(), text: 'يرجى إدخال بياناتك:' }]); };

  const submitForm = async () => {
    if (!humanData.name || !humanData.phone) return;
    try {
      const r = await safePost(`${API_URL}/api/support/request`, { ...humanData, session_id: sessionId, user_id: user?._id || '' });
      if (r.chat_id) { setLiveChatId(r.chat_id); setActiveTab(r.chat_id); }
    } catch {}
    setHumanStep('waiting');
    setMessages(p => [...p, { id: Date.now(), role: 'assistant', time: new Date().toISOString(), text: `شكراً ${humanData.name}! ✅\n\nيتم تحويلك الآن إلى ممثل خدمة العملاء.\n⏱ الوقت المتوقع: 3-5 دقائق` }]);
  };

  const sendLiveMessage = async (text) => {
    const t = (text || input).trim(); if (!t || !liveChatId) return;
    setInput('');
    await safePost(`${API_URL}/api/live-chat/${liveChatId}/message`, { sender: 'visitor', text: t });
  };

  const uploadImage = async (file) => {
    const form = new FormData(); form.append('file', file);
    try {
      const r = await fetch(`${API_URL}/api/upload/chat-image`, { method: 'POST', body: form });
      const d = await r.json();
      if (d.url) {
        if (activeTab !== 'ai' && liveChatId) { await safePost(`${API_URL}/api/live-chat/${liveChatId}/message`, { sender: 'visitor', text: `[image:${d.url}]` }); }
        else { setMessages(p => [...p, { id: Date.now(), role: 'user', time: new Date().toISOString(), text: `[image:${d.url}]` }]); }
      }
    } catch {}
  };

  const submitRating = async () => {
    if (!liveChatId || !ratingValue) return;
    await safePost(`${API_URL}/api/live-chat/${liveChatId}/rate`, { rating: ratingValue });
    setHumanStep(null); setLiveChatId(null); setActiveTab('ai'); setShowTopics(true); setRatingValue(0);
  };

  const closeTab = async (tabId) => {
    try { fetch(`${API_URL}/api/admin/live-chats/${tabId}/close`, { method: 'POST' }); } catch {}
    const newClosed = [...closedTabIds, tabId];
    setClosedTabIds(newClosed);
    sessionStorage.setItem('ss_closed_tabs', JSON.stringify(newClosed));
    setChatHistory(p => p.filter(c => c.id !== tabId));
    if (activeTab === tabId) { setActiveTab('ai'); setLiveChatId(null); setHumanStep(null); setShowTopics(true); }
  };

  const sendMessage = async (text) => {
    const msg = (text || input).trim(); if (!msg || loading) return;
    if (isHumanRequest(msg)) { startHumanFlow(); return; }
    setShowTopics(false);
    setMessages(p => [...p, { id: Date.now(), role: 'user', text: msg, time: new Date().toISOString() }]);
    setInput(''); setLoading(true);
    try {
      const data = await safePost(`${API_URL}/api/chat/smart`, { message: msg, session_id: sessionId, user_id: user?._id || '' });
      setMessages(p => [...p, { id: Date.now()+1, role: 'assistant', text: data.response, time: new Date().toISOString() }]);
    } catch { setMessages(p => [...p, { id: Date.now()+1, role: 'assistant', time: new Date().toISOString(), text: 'عذراً، حدث خطأ. 🔄' }]); }
    setLoading(false);
  };

  const renderMsg = (msg, i) => {
    const isImg = msg.text?.startsWith('[image:');
    const imgUrl = isImg ? msg.text.slice(7, -1) : null;
    const isSystem = msg.sender === 'system' || msg.role === 'system';
    if (isSystem) return <div key={i} className="text-center py-2"><span className="text-[11px] bg-primary/10 text-primary px-3 py-1 rounded-full">{msg.text}</span></div>;
    const isBot = msg.role === 'assistant' || msg.sender === 'agent';
    const isVisitor = msg.role === 'user' || msg.sender === 'visitor';
    return (
      <div key={i} className={`flex gap-2 ${isVisitor ? 'flex-row-reverse' : ''}`}>
        <div className="flex-shrink-0 mt-1">
          {isBot ? <div className="w-6 h-6 rounded-lg bg-primary/10 flex items-center justify-center"><Sparkles className="w-3 h-3 text-primary" /></div>
            : <div className="w-6 h-6 rounded-lg bg-blue-100 flex items-center justify-center"><User className="w-3 h-3 text-blue-600" /></div>}
        </div>
        <div className={`max-w-[80%] px-3 py-2 ${isBot ? 'bg-white border border-gray-200 rounded-2xl rounded-tl-md shadow-sm' : 'bg-primary text-white rounded-2xl rounded-tr-md'}`}>
          {isImg ? <img src={imgUrl} alt="" className="max-w-[200px] rounded-lg" /> : <p className={`text-[13px] leading-[1.6] whitespace-pre-wrap ${isBot ? 'text-gray-700' : 'text-white'}`}>{msg.text}</p>}
        </div>
      </div>
    );
  };

  const activeLiveChats = chatHistory.filter(c => c.status === 'active' || c.status === 'waiting');
  const closedLiveChats = chatHistory.filter(c => c.status === 'closed');
  const REASON_LABELS = { general:'استفسار عام', technical:'مشكلة تقنية', coins:'شراء عملات', order:'طلب/شحنة', commission:'عمولات', account:'حساب' };
  const allTabs = [{ id: 'ai', label: 'AI Assistant' }, ...chatHistory.map(c => ({ id: c.id, label: c.agent_name || 'Support', status: c.status, photo: c.agent_photo, category: c.category, created: c.created_at }))];
  const hasLiveTabs = allTabs.length > 1;

  return (
    <>
      {/* FAB */}
      <AnimatePresence>
        {!open && (
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }} transition={{ type: 'spring', damping: 15, delay: 0.3 }}
            className="fixed bottom-24 lg:bottom-6 left-4 lg:left-6 z-40" style={{ direction: 'ltr' }}>
            <button onClick={() => setOpen(true)} data-testid="chat-fab-button"
              className="relative w-14 h-14 rounded-full flex items-center justify-center bg-gradient-to-br from-primary to-emerald-500 shadow-xl shadow-primary/30 border border-white/20">
              <MessageCircle className="w-6 h-6 text-white" />
              <span className="absolute top-0 right-0 w-3.5 h-3.5 bg-green-400 rounded-full border-2 border-white" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* MAIN CHAT LAYOUT */}
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: 60, scale: 0.9 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 60, scale: 0.9 }}
            transition={{ type: 'spring', damping: 22, stiffness: 250 }}
            className="fixed bottom-20 lg:bottom-4 left-2 right-2 sm:left-4 sm:right-auto z-50 flex gap-0"
            style={{ direction: 'ltr', maxHeight: 'calc(100vh - 10rem)', height: '540px' }}>

            {/* CHAT WINDOW */}
            <div className="flex flex-col overflow-hidden shadow-2xl rounded-2xl border border-gray-200 bg-white w-full sm:w-[400px] flex-shrink-0">
              {/* HEADER */}
              <div className="flex-shrink-0 bg-gradient-to-r from-primary to-emerald-500 p-3 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-xl overflow-hidden bg-white/20 flex items-center justify-center">
                    {activeTab !== 'ai' && liveData.agent_photo ? <img src={liveData.agent_photo} alt="" className="w-full h-full object-cover" /> : <SSLogoIcon size={20} />}
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-sm">{activeTab !== 'ai' && liveData.agent_name ? liveData.agent_name : 'S&S Assistant'}</h3>
                    <span className="text-white/60 text-[10px]">{humanStep === 'live' ? 'Live Agent' : humanStep === 'waiting' ? 'Connecting...' : 'Online'}</span>
                  </div>
                </div>
                <div className="flex gap-1">
                  {!humanStep && <button onClick={startHumanFlow} className="w-7 h-7 rounded-lg bg-white/15 flex items-center justify-center hover:bg-white/25"><Headphones className="w-3.5 h-3.5 text-white" /></button>}
                  <button onClick={() => { setOpen(false); }} className="w-7 h-7 rounded-lg bg-white/15 flex items-center justify-center hover:bg-white/25"><X className="w-3.5 h-3.5 text-white" /></button>
                </div>
              </div>

              {/* MOBILE TABS */}
              {hasLiveTabs && (
                <div className="flex-shrink-0 flex gap-1 bg-white border-b border-gray-200 px-2 py-1.5 overflow-x-auto scrollbar-hide sm:hidden">
                  {allTabs.map(t => (
                    <div key={t.id} className={`flex items-center flex-shrink-0 rounded-lg ${activeTab === t.id ? 'bg-primary/10 ring-1 ring-primary/30' : 'bg-gray-100'}`}>
                      <button onClick={() => { setActiveTab(t.id); if (t.id !== 'ai') setLiveChatId(t.id); }}
                        className={`px-2.5 py-1 text-[10px] font-bold flex items-center gap-1 ${activeTab === t.id ? 'text-primary' : 'text-gray-500'}`}>
                        {t.id === 'ai' ? <><Sparkles className="w-3 h-3" /> AI</> : <><Headphones className="w-3 h-3" /> {t.label}</>}
                      </button>
                      {t.id !== 'ai' && <button onClick={(e) => { e.stopPropagation(); closeTab(t.id); }} className="pr-1 text-gray-400 hover:text-red-500"><X className="w-2.5 h-2.5" /></button>}
                    </div>
                  ))}
                </div>
              )}

            {/* MESSAGES AREA */}
            <div className="flex-1 overflow-y-auto bg-gray-50" style={{ scrollbarWidth: 'thin' }}>
              <div className="p-3 space-y-2.5">
                {activeTab === 'ai' ? (
                  <>
                    {messages.map((m, i) => renderMsg(m, i))}
                    {humanStep === 'type' && (
                      <div className="flex gap-2 justify-center pt-1">
                        <button onClick={() => selectType(true)} className="flex-1 p-2.5 rounded-xl bg-white border border-primary/30 hover:bg-primary/5 text-center"><p className="text-xs font-bold text-gray-700">نعم، تاجر مسجّل</p></button>
                        <button onClick={() => selectType(false)} className="flex-1 p-2.5 rounded-xl bg-white border border-gray-200 hover:bg-gray-50 text-center"><p className="text-xs font-bold text-gray-700">لا، أدرس الموضوع</p></button>
                      </div>
                    )}
                    {humanStep === 'reason' && (
                      <div className="grid grid-cols-2 gap-1.5">{SUPPORT_REASONS.map((r,i) => (
                        <button key={i} onClick={() => selectReason(r)} className="flex items-center gap-1.5 p-2 rounded-xl bg-white border border-gray-200 hover:border-primary/30 text-right">
                          <r.icon className="w-3.5 h-3.5 text-primary flex-shrink-0" /><span className="text-[10px] font-medium text-gray-600">{r.text}</span>
                        </button>
                      ))}</div>
                    )}
                    {humanStep === 'form' && (
                      <div className="bg-white border border-primary/20 rounded-xl p-3 space-y-2">
                        <input value={humanData.name} onChange={e => setHumanData(p => ({...p, name: e.target.value}))} placeholder="الاسم *" className="w-full h-9 px-3 text-sm rounded-lg border border-gray-200 bg-gray-50 focus:border-primary outline-none" />
                        <input value={humanData.phone} onChange={e => setHumanData(p => ({...p, phone: e.target.value}))} placeholder="رقم الهاتف *" className="w-full h-9 px-3 text-sm rounded-lg border border-gray-200 bg-gray-50 focus:border-primary outline-none" />
                        <input value={humanData.email} onChange={e => setHumanData(p => ({...p, email: e.target.value}))} placeholder="الإيميل (اختياري)" className="w-full h-9 px-3 text-sm rounded-lg border border-gray-200 bg-gray-50 focus:border-primary outline-none" />
                        <button onClick={submitForm} disabled={!humanData.name || !humanData.phone} className="w-full h-10 rounded-lg bg-primary text-white text-sm font-semibold disabled:opacity-40 flex items-center justify-center gap-2"><Send className="w-4 h-4" /> تحويلي لموظف</button>
                      </div>
                    )}
                    {humanStep === 'waiting' && <div className="flex flex-col items-center py-4 gap-2"><Loader2 className="w-7 h-7 text-primary animate-spin" /><p className="text-[11px] text-gray-500">جارٍ البحث عن موظف... ⏱ 3-5 دقائق</p></div>}
                    {showTopics && messages.length <= 1 && !loading && !humanStep && (
                      <div className="pt-1">
                        <div className="grid grid-cols-2 gap-1.5">
                          {QUICK_TOPICS.map((q,i) => (
                            <button key={i} onClick={() => sendMessage(q.text)} className="flex items-center gap-1.5 p-2 rounded-xl bg-white border border-gray-200 hover:border-primary/30 text-right">
                              <div className={`w-6 h-6 rounded-lg ${q.color} flex items-center justify-center flex-shrink-0`}><q.icon className="w-3 h-3 text-white" /></div>
                              <span className="text-[10px] text-gray-600 font-medium">{q.text}</span>
                            </button>
                          ))}
                          <button onClick={startHumanFlow} className="flex items-center gap-1.5 p-2 rounded-xl bg-orange-50 border border-orange-200 text-right col-span-2">
                            <div className="w-6 h-6 rounded-lg bg-orange-500 flex items-center justify-center flex-shrink-0"><Headphones className="w-3 h-3 text-white" /></div>
                            <span className="text-[10px] text-orange-700 font-bold">أريد التحدث مع موظف</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {/* Live chat tab content */}
                    {(liveData.messages || []).map((m, i) => renderMsg(m, i))}
                    {liveData.status === 'closed' && humanStep === 'closed' && (
                      <div className="bg-white border border-gray-200 rounded-xl p-4 text-center space-y-3">
                        <p className="text-sm font-bold text-gray-700">تم إنهاء المحادثة مع {liveData.agent_name}</p>
                        <p className="text-xs text-gray-500">هل تم حل مشكلتك؟ قيّم تجربتك:</p>
                        <div className="flex justify-center gap-1">
                          {[1,2,3,4,5].map(s => (
                            <button key={s} onClick={() => setRatingValue(s)} className={`p-1 transition-colors ${ratingValue >= s ? 'text-amber-400' : 'text-gray-300'}`}>
                              <Star className="w-7 h-7" fill={ratingValue >= s ? 'currentColor' : 'none'} />
                            </button>
                          ))}
                        </div>
                        {ratingValue > 0 && <button onClick={submitRating} className="h-9 px-6 rounded-lg bg-primary text-white text-sm font-semibold">إرسال التقييم</button>}
                        <p className="text-[10px] text-gray-400">لأي استفسار إضافي، افتح طلب جديد من تاب AI</p>
                      </div>
                    )}
                  </>
                )}
                {loading && <div className="flex gap-2"><div className="w-6 h-6 rounded-lg bg-primary/10 flex items-center justify-center"><Sparkles className="w-3 h-3 text-primary" /></div><div className="bg-white border border-gray-200 rounded-2xl rounded-tl-md px-3 py-2"><div className="flex gap-1 items-center">{[0,1,2].map(i => <motion.div key={i} className="w-1.5 h-1.5 rounded-full bg-primary" animate={{ y:[0,-5,0] }} transition={{ repeat:Infinity, duration:0.5, delay:i*0.15 }} />)}</div></div></div>}
                <div ref={messagesEndRef} />
              </div>
            </div>

            {/* INPUT */}
            <div className="flex-shrink-0 bg-white border-t border-gray-200 p-2.5">
              <div className="flex items-center gap-1.5">
                <button onClick={() => fileInputRef.current?.click()} className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center hover:bg-gray-200 flex-shrink-0">
                  <ImageIcon className="w-4 h-4 text-gray-500" />
                </button>
                <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={e => { if (e.target.files[0]) uploadImage(e.target.files[0]); e.target.value=''; }} />
                <textarea id="chat-input-main" value={input} onChange={e => setInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); if (activeTab !== 'ai' && humanStep === 'live') sendLiveMessage(); else sendMessage(); }}}
                  placeholder={humanStep === 'live' ? 'اكتب للموظف...' : 'اكتب رسالتك...'}
                  rows={1}
                  className="flex-1 bg-gray-100 border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:border-primary/40 resize-none max-h-20"
                  style={{ minHeight: '36px' }}
                  disabled={loading || (humanStep && humanStep !== 'live' && humanStep !== 'closed' && activeTab === 'ai')} />
                <button onClick={() => { if (activeTab !== 'ai' && humanStep === 'live') sendLiveMessage(); else sendMessage(); }}
                  disabled={!input.trim() || loading} className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center disabled:opacity-30">
                  <Send className="w-4 h-4 text-white" />
                </button>
              </div>
            </div>
            </div>

            {/* RIGHT SIDE CONVERSATIONS PANEL */}
            {hasLiveTabs && (
              <div className="hidden sm:flex flex-col flex-shrink-0 ml-2" style={{ width: panelExpanded ? '200px' : '44px' }}>
                <div className="h-full bg-white rounded-2xl shadow-2xl border border-gray-200/80 overflow-hidden flex flex-col transition-all duration-200">
                  {/* Toggle button */}
                  <button onClick={() => setPanelExpanded(p => !p)}
                    className="flex-shrink-0 p-2 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white flex items-center justify-center hover:bg-gray-100 transition-colors">
                    {panelExpanded ? (
                      <div className="flex items-center justify-between w-full px-1">
                        <p className="text-[11px] font-bold text-gray-600 flex items-center gap-1.5">
                          <MessageCircle className="w-3.5 h-3.5 text-primary" /> Chats
                        </p>
                        <span className="text-[9px] font-mono bg-primary/10 text-primary px-1.5 py-0.5 rounded-full">{allTabs.length - 1}</span>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-0.5">
                        <MessageCircle className="w-4 h-4 text-primary" />
                        <span className="text-[8px] font-bold text-primary">{allTabs.length - 1}</span>
                      </div>
                    )}
                  </button>
                  {/* Tabs */}
                  <div className="flex-1 overflow-y-auto p-1.5 space-y-1">
                    {allTabs.map((t, idx) => (
                      <motion.div key={t.id} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                        onClick={() => { setActiveTab(t.id); if (t.id !== 'ai') setLiveChatId(t.id); }}
                        className={`relative p-2 rounded-xl cursor-pointer transition-all ${activeTab === t.id ? 'bg-primary/8 shadow-md ring-1 ring-primary/20' : 'hover:bg-gray-50'}`}>
                        {activeTab === t.id && <div className="absolute left-0 top-2 bottom-2 w-[3px] rounded-full bg-gradient-to-b from-primary to-emerald-400" />}
                        {panelExpanded ? (
                          <div className="flex items-center gap-2">
                            {t.id === 'ai' ? (
                              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-primary/15 to-emerald-400/15 flex items-center justify-center flex-shrink-0 border border-primary/10">
                                <Sparkles className="w-4 h-4 text-primary" />
                              </div>
                            ) : (
                              <div className="relative flex-shrink-0">
                                <div className="w-9 h-9 rounded-lg overflow-hidden ring-1 ring-gray-200">
                                  {t.photo ? <img src={t.photo} alt="" className="w-full h-full object-cover" />
                                    : <div className="w-full h-full bg-orange-50 flex items-center justify-center"><Headphones className="w-4 h-4 text-orange-500" /></div>}
                                </div>
                                <span className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white ${t.status === 'active' ? 'bg-green-500' : t.status === 'waiting' ? 'bg-amber-500 animate-pulse' : 'bg-gray-300'}`} />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className={`text-[10px] font-bold truncate ${activeTab === t.id ? 'text-primary' : 'text-gray-700'}`}>{t.id === 'ai' ? 'AI' : t.label}</p>
                              {t.id !== 'ai' && <p className={`text-[8px] ${t.status === 'active' ? 'text-green-500' : t.status === 'waiting' ? 'text-amber-500' : 'text-gray-400'}`}>{t.status === 'active' ? 'Live' : t.status === 'waiting' ? 'Waiting' : 'Ended'}</p>}
                            </div>
                            {t.id !== 'ai' && (
                              <button onClick={(e) => { e.stopPropagation(); closeTab(t.id); }}
                                className="w-5 h-5 rounded-full bg-gray-100 hover:bg-red-100 flex items-center justify-center flex-shrink-0">
                                <X className="w-3 h-3 text-gray-400 hover:text-red-500" />
                              </button>
                            )}
                          </div>
                        ) : (
                          <div className="flex flex-col items-center">
                            {t.id === 'ai' ? <Sparkles className="w-5 h-5 text-primary" />
                              : <div className="relative">
                                  <div className="w-8 h-8 rounded-lg overflow-hidden ring-1 ring-gray-200">
                                    {t.photo ? <img src={t.photo} alt="" className="w-full h-full object-cover" />
                                      : <Headphones className="w-4 h-4 text-orange-500 m-auto mt-2" />}
                                  </div>
                                  <span className={`absolute -bottom-0.5 -right-0.5 w-2 h-2 rounded-full border border-white ${t.status === 'active' ? 'bg-green-500' : 'bg-amber-500'}`} />
                                </div>}
                          </div>
                        )}
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
